import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ViewContainerRef,
  ComponentRef,
} from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";

export interface TableColumn {
  header: string;
  field: string;
  customTemplate?: TemplateRef<any>;
  width?: string;
  sortable?: boolean;
}

export interface PaginationConfig {
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  pageSizeOptions: number[];
  showPagination?: boolean;
}

export interface TableConfig {
  showSerialNumber?: boolean;
  serialNumberLabel?: string;
  serialNumberStartFrom?: number;
}

@Component({
  selector: "app-admin-common-table",
  templateUrl: "./admin-common-table.component.html",
  styleUrls: ["./admin-common-table.component.scss"],
})
export class AdminCommonTableComponent implements OnChanges {
  @Input() tableHeaders: TableColumn[] = [];
  @Input() tableData: any[] = [];
  @Input() customClass: string[] = [];
  @Input() paginationConfig: PaginationConfig = {
    totalItems: 0,
    currentPage: 1,
    itemsPerPage: 10,
    pageSizeOptions: [10, 25, 50],
    showPagination: true,
  };
  @Input() tableConfig: TableConfig = {
    showSerialNumber: false,
    serialNumberLabel: "S.No.",
    serialNumberStartFrom: 1,
  };
  @Input() loading: boolean = false;
  @Input() emptyMessage: string = "No data available";

  @Output() pageEvent = new EventEmitter<{ page: number; pageSize: number }>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() sortEvent = new EventEmitter<{
    field: string;
    direction: "asc" | "desc";
  }>();

  currentSortField: string = "";
  currentSortDirection: "asc" | "desc" = "asc";
  skeletonRows: number[] = [1, 2, 3, 4, 5];

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  onPageChange(event: { page: number; pageSize: number }) {
    this.pageEvent.emit({
      page: event.page,
      pageSize: event.pageSize,
    });
  }

  onRowClick(row: any) {
    this.rowClick.emit(row);
  }

  onSort(field: string) {
    if (this.currentSortField === field) {
      this.currentSortDirection =
        this.currentSortDirection === "asc" ? "desc" : "asc";
    } else {
      this.currentSortField = field;
      this.currentSortDirection = "asc";
    }

    this.sortEvent.emit({
      field: this.currentSortField,
      direction: this.currentSortDirection,
    });
  }

  onMatSort(event: Sort) {
    if (!event.active || !event.direction) {
      return;
    }
    this.currentSortField = event.active;
    this.currentSortDirection = event.direction as "asc" | "desc";
    this.sortEvent.emit({
      field: this.currentSortField,
      direction: this.currentSortDirection,
    });
  }

  getSortIcon(field: string): string {
    if (this.currentSortField !== field) {
      return "unfold_more";
    }
    return this.currentSortDirection === "asc"
      ? "keyboard_arrow_up"
      : "keyboard_arrow_down";
  }

  get paginatedData(): any[] {
    if (!this.paginationConfig.showPagination) {
      return this.tableData;
    }

    const startIndex =
      (this.paginationConfig.currentPage - 1) *
      this.paginationConfig.itemsPerPage;
    const endIndex = startIndex + this.paginationConfig.itemsPerPage;
    return this.tableData.slice(startIndex, endIndex);
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  getSerialNumber(index: number): number {
    if (!this.paginationConfig.showPagination) {
      return this.tableConfig.serialNumberStartFrom! + index;
    }

    const startIndex =
      (this.paginationConfig.currentPage - 1) *
      this.paginationConfig.itemsPerPage;
    return this.tableConfig.serialNumberStartFrom! + startIndex + index;
  }

  get displayHeaders(): TableColumn[] {
    if (!this.tableConfig.showSerialNumber) {
      return this.tableHeaders;
    }

    const serialNumberColumn: TableColumn = {
      header: this.tableConfig.serialNumberLabel!,
      field: "serialNumber",
      width: "65px",
      sortable: false,
    };

    return [serialNumberColumn, ...this.tableHeaders];
  }

  get displayedColumnFields(): string[] {
    return this.displayHeaders.map((h) => h.field);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["tableData"]) {
      this.dataSource.data = this.tableData || [];
      // For server-side pagination, do not attach paginator to dataSource
    }
  }
}
